/**
 * Constants from `docs/DESIGN.md`, and the config a mode may override.
 *
 * **Time.** The engine has no clock (BASELINE 2), and nothing in hashed state
 * may be a float (BASELINE 1). So its unit of time is the *tick*: 1/60 s, an
 * integer. Durations are still written in milliseconds here, because that is
 * how the spec writes them, and `msToTicks` converts once. The caller owns the
 * wall clock and decides how many ticks to advance; an input event's `frame`
 * is a tick number.
 *
 * **Gravity is fixed-point.** A speed is an integer number of thousandths
 * (`GRAVITY_PERMILLE` is 1x), and a piece's accumulator counts millionths of a
 * tick (`GRAVITY_UNITS_PER_TICK`). A millionth and not a thousandth, because
 * two per-mille factors are multiplied together — the mode's speed and a
 * per-tick modifier such as a handicap — and their product has to be an
 * integer: 1500 x 925 is 1,387,500 millionths, which thousandths cannot hold.
 * Every number in `EngineConfig` is an integer, and `createConfig` refuses one
 * that is not. `docs/decisions/0050`.
 */
import type { CreateGameOptions, EngineConfig, GameState, GravityConfig } from './types.js';

/** Ticks per second. The fixed timestep the engine steps by. */
export const TICKS_PER_SECOND = 60;

/** Milliseconds in one tick. Exported for callers converting wall time. */
export const TICK_MS = 1000 / TICKS_PER_SECOND;

export const DEFAULT_WIDTH = 10;
export const DEFAULT_VISIBLE_ROWS = 20;

/**
 * Rows above the visible playfield.
 *
 * The prototype spawns at `y = -1` into unbounded empty space above a 20-row
 * array. A hashable board has to be finite, so the engine reserves two rows:
 * the lower one holds the top of a spawning J, L, O, S, T or Z exactly where
 * the prototype puts it, and the upper one is headroom for a kick that lifts
 * the piece. Anything above them is wall.
 */
const DEFAULT_HIDDEN_ROWS = 2;

/** Lock delay, `docs/DESIGN.md`: 500ms. */
const DEFAULT_LOCK_DELAY_MS = 500;

/** At most 15 move-or-rotate lock-delay resets per piece. */
const DEFAULT_MAX_LOCK_RESETS = 15;

/** How many upcoming pieces the client may preview. */
const DEFAULT_NEXT_PREVIEW = 5;

/** A gravity speed, or a per-tick modifier, of exactly 1x, in thousandths. */
export const GRAVITY_PERMILLE = 1000;

/**
 * Accumulator units in one tick of gravity at 1x: a thousandth of a
 * thousandth. A piece falls one row each time its accumulator reaches the
 * level's interval in ticks times this.
 */
export const GRAVITY_UNITS_PER_TICK = GRAVITY_PERMILLE * GRAVITY_PERMILLE;

/** Gravity curve of the reference prototype: 700ms at level 0, floor of 90ms. */
export const DEFAULT_GRAVITY: GravityConfig = Object.freeze({
  baseMs: 700,
  perLevelMs: 60,
  minMs: 90,
  linesPerLevel: 10,
  speedPermille: GRAVITY_PERMILLE,
});

/**
 * Milliseconds to whole ticks, rounded.
 *
 * `ms * 60` is an exact integer for an integer `ms`; the division by 1000 is
 * one correctly rounded IEEE 754 operation and `Math.round` is specified
 * exactly, so the result is the same integer on every platform.
 */
export function msToTicks(ms: number): number {
  return Math.round((ms * TICKS_PER_SECOND) / 1000);
}

/** Ticks to milliseconds. Convenience for HUDs; never used in hashed state. */
export function ticksToMs(ticks: number): number {
  return (ticks * 1000) / TICKS_PER_SECOND;
}

/**
 * An integer the hash can write exactly, and no smaller than `min`.
 *
 * A fraction in the config would be a fraction in the hash stream, and the
 * hash writes integers. Refusing it here, where a game is made, is what lets
 * everything downstream assume it.
 */
function wholeNumber(name: string, value: number, min: number): number {
  if (!Number.isSafeInteger(value) || value < min) {
    throw new RangeError(
      `engine config: ${name} must be an integer of at least ${String(min)}, got ${String(value)}`,
    );
  }
  return value;
}

/** Fill in every default a mode did not override, check it, and freeze the result. */
export function createConfig(options: CreateGameOptions): EngineConfig {
  const overrides = options.gravity ?? {};
  const gravity: GravityConfig = Object.freeze({
    baseMs: wholeNumber('gravity.baseMs', overrides.baseMs ?? DEFAULT_GRAVITY.baseMs, 0),
    perLevelMs: wholeNumber(
      'gravity.perLevelMs',
      overrides.perLevelMs ?? DEFAULT_GRAVITY.perLevelMs,
      0,
    ),
    minMs: wholeNumber('gravity.minMs', overrides.minMs ?? DEFAULT_GRAVITY.minMs, 0),
    linesPerLevel: wholeNumber(
      'gravity.linesPerLevel',
      overrides.linesPerLevel ?? DEFAULT_GRAVITY.linesPerLevel,
      0,
    ),
    speedPermille: wholeNumber(
      'gravity.speedPermille',
      overrides.speedPermille ?? DEFAULT_GRAVITY.speedPermille,
      0,
    ),
  });

  return Object.freeze({
    seed: wholeNumber('seed', options.seed, Number.MIN_SAFE_INTEGER),
    width: wholeNumber('width', options.width ?? DEFAULT_WIDTH, 1),
    visibleRows: wholeNumber('visibleRows', options.visibleRows ?? DEFAULT_VISIBLE_ROWS, 1),
    hiddenRows: wholeNumber('hiddenRows', options.hiddenRows ?? DEFAULT_HIDDEN_ROWS, 0),
    groupGravity: options.groupGravity ?? false,
    lockDelayMs: wholeNumber('lockDelayMs', options.lockDelayMs ?? DEFAULT_LOCK_DELAY_MS, 0),
    maxLockResets: wholeNumber(
      'maxLockResets',
      options.maxLockResets ?? DEFAULT_MAX_LOCK_RESETS,
      0,
    ),
    nextPreview: wholeNumber('nextPreview', options.nextPreview ?? DEFAULT_NEXT_PREVIEW, 0),
    gravity,
  });
}

/** Lock delay in ticks. */
export function lockDelayTicks(config: EngineConfig): number {
  return Math.max(1, msToTicks(config.lockDelayMs));
}

/** Level, from lines cleared. */
export function currentLevel(state: GameState): number {
  const per = state.config.gravity.linesPerLevel;
  if (per <= 0) return 0;
  return Math.floor(state.lines / per);
}

/**
 * Ticks between gravity steps at the current level.
 *
 * `max(minMs, baseMs - level * perLevelMs)`, then rounded to whole ticks and
 * floored at one tick so gravity can never stall.
 */
export function gravityIntervalTicks(state: GameState): number {
  const gravity = state.config.gravity;
  const ms = Math.max(gravity.minMs, gravity.baseMs - currentLevel(state) * gravity.perLevelMs);
  return Math.max(1, msToTicks(ms));
}

/** Accumulator units a piece needs before it falls one row, at the current level. */
export function gravityThresholdUnits(state: GameState): number {
  return gravityIntervalTicks(state) * GRAVITY_UNITS_PER_TICK;
}

/** Accumulator units one tick adds at this game's own speed, with no modifier. */
export function gravityUnitsPerTick(config: EngineConfig): number {
  return config.gravity.speedPermille * GRAVITY_PERMILLE;
}
